import React, { FC, useEffect, useRef, Fragment, useState, useContext, useCallback, ReactNode } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { produce } from 'immer';

import TrashIcon from '@ad-scripts/presentation/components/common/icons/trash';
import { H4 } from '@ad-scripts/presentation/components/common/headers';

import FormContext from 'src/presentation/contexts/FormContext';
import Textarea from 'src/presentation/components/forms/Textarea';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { PrimaryButtonBase } from '@ad-scripts/presentation/components/common/button';
import { SIDE_PANEL_ROW_TYPES } from '../constants/ivrConstants';
import { immutablyDeleteArrayItem } from '../helpers/ivrHelpers';

type OptionComponentProps = {
  'data-aid'?: string;
  actionRow?: FC;
  attrList: string;
  className?: string;
  currentIndex: number;
  disabled?: boolean;
  id?: string;
  onDeleteOption?: Function;
  readOnly?: boolean;
  removeOptionIdByIndex: Function;
  selectedIndex?: number;
};

const TransitionPanel: FC<any> = ({ children, childrenRef, currentIndex, selectedIndex }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (childrenRef.current && currentIndex === selectedIndex) {
      // { behaviour: 'smooth' } has been removed due to a problem in recent versions of Chrome
      // that breaks scrolling in this particular case.
      // Works fine with { behaviour: 'smooth' } in Firefox
      // https://bugs.chromium.org/p/chromium/issues/list?q=scrollintoview%20smooth&can=2
      childrenRef.current.scrollIntoView({ block: 'start' });
      setAnimate(true);
    }
  }, [currentIndex, childrenRef, selectedIndex]);

  return (
    <CSSTransition in={animate} timeout={2000} classNames="fade" appear enter onEntered={() => setAnimate(false)}>
      {children}
    </CSSTransition>
  );
};

const OptionActionRow = ({ onDelete, id }) => {
  return (
    <ActionRow>
      <DeleteButton data-aid={`${id}-deleteButton`} onDelete={onDelete} />
    </ActionRow>
  );
};

export const DeleteButton = ({ onDelete, 'data-aid': dataAid }) => (
  <Button data-aid={dataAid} type={Button.Types.DANGER} onClick={onDelete}>
    Delete
  </Button>
);

const OptionContainer: FC<OptionComponentProps> = ({
  'data-aid': dataAid,
  actionRow = OptionActionRow as ({ onDelete, id, index }: { onDelete; id; index? }) => JSX.Element | null,
  attrList,
  children,
  className,
  currentIndex,
  disabled,
  id,
  onDeleteOption,
  readOnly,
  removeOptionIdByIndex,
  selectedIndex,
}) => {
  const filledWrapperRef = useRef<any>();
  const { updateState, workingState, onBlur } = useContext(FormContext);

  const ActionRowElement = actionRow;

  const onDelete = useCallback(() => {
    removeOptionIdByIndex(currentIndex);
    if (onDeleteOption) {
      onDeleteOption(currentIndex, attrList);
      return;
    }

    const items = immutablyDeleteArrayItem(get(workingState, attrList, []), currentIndex);

    updateState(set({}, attrList, items), { replaceArrayValues: true });
    onBlur && onBlur({ currentState: produce(workingState, draftState => set(draftState, attrList, items)) });
  }, [attrList, currentIndex, onBlur, onDeleteOption, removeOptionIdByIndex, updateState, workingState]);

  return (
    <TransitionPanel childrenRef={filledWrapperRef} currentIndex={currentIndex} selectedIndex={selectedIndex}>
      <FilledWrapper className={className} ref={filledWrapperRef} data-aid={`${dataAid}`} role="listitem">
        {children}
        {!readOnly && !disabled && !!actionRow && <ActionRowElement id={id} onDelete={onDelete} index={currentIndex} />}
      </FilledWrapper>
    </TransitionPanel>
  );
};

type ScrollRowProps = {
  scrollToIndex?: number;
  type: SIDE_PANEL_ROW_TYPES;
};

const ScrollRow: FC<ScrollRowProps> = ({ scrollToIndex, type, children }) => {
  const errorRowRef = useRef<HTMLDivElement>();

  return (
    <TransitionPanel childrenRef={errorRowRef} currentIndex={scrollToIndex} selectedIndex={type}>
      <Row ref={errorRowRef}>{children}</Row>
    </TransitionPanel>
  );
};

type NodeContainerProps = {
  scrollToOption?: boolean;
  parentRef: any;
};

const NodeContainer: FC<NodeContainerProps> = ({ scrollToOption, parentRef, children }) => {
  useEffect(() => {
    if (get(parentRef, 'current') && !scrollToOption) {
      parentRef.current.scrollTop = 0;
    }
  }, [parentRef, scrollToOption]);

  return <Fragment>{children}</Fragment>;
};

type ContainerForOptionsProps = {
  'data-aid'?: string;
  addButtonLabel?: string;
  context: ReactNode;
  description?: string;
  disabled?: boolean;
  label: string;
  onAddButtonClick?: Function;
  addButtonRender?: Function;
};

const ContainerForOptions = ({
  'data-aid': dataAid,
  addButtonLabel,
  context,
  description,
  disabled,
  label,
  onAddButtonClick,
  addButtonRender,
}: ContainerForOptionsProps) => {
  const addButton = addButtonRender ? (
    addButtonRender({ 'data-aid': 'addOption', onAddButtonClick: onAddButtonClick })
  ) : (
    <AddButton data-aid="addOption" label={addButtonLabel} onAddButtonClick={onAddButtonClick} />
  );

  return (
    <OptionContainerBox data-aid={dataAid}>
      {label && (
        <Fragment>
          <SecondaryHeaderRow>
            <SecondaryHeader>{label}</SecondaryHeader>
            {description && <SecondaryDescription>{description}</SecondaryDescription>}
          </SecondaryHeaderRow>
        </Fragment>
      )}
      <OptionsContainer data-aid="options-container" role="list">
        {context}
      </OptionsContainer>
      {addButtonLabel && !disabled && <>{addButton}</>}
    </OptionContainerBox>
  );
};

const AddButton = ({ 'data-aid': dataAid, label, onAddButtonClick }) => {
  return (
    <OptionsActionRow>
      <SecondaryButton data-aid={dataAid} onClick={onAddButtonClick}>
        {label}
      </SecondaryButton>
    </OptionsActionRow>
  );
};

export { AddButton, ContainerForOptions, NodeContainer, OptionContainer, ScrollRow };

const OptionContainerBox = styled.div``;

export const ActionRow = styled.div`
  display: flex;
  justify-content: end;
  padding: 5px 14px;

  & > button:not(:last-child) {
    margin-right: 10px;
  }
`;

export const StyledTrashIcon = styled(TrashIcon)`
  cursor: pointer;
  height: 16px;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 16px;
`;

export const SecondaryHeader = styled(H4)`
  font-weight: bold;
  padding: 12px 0;
  margin: 0;
`;

export const SecondaryDescription = styled.p`
  color: ${p => p.theme.colors.gray700};
  margin-top: 0;
  margin-bottom: ${p => p.theme.spacing.stackXSmall};
`;

export const Row = styled.div`
  padding: 5px 14px;
  display: flex;
  margin-bottom: 0;
  border-radius: 5px;

  &.fade-enter {
    background: ${p => p.theme.colors.white};
  }

  &.fade-enter-active {
    background: #f2f0f8;
  }

  &.fade-enter-active {
    transition: background 500ms;
  }
`;

export const MultiContentRow = styled(Row)`
  flex-direction: column;

  & > div:not(:last-child) {
    margin-bottom: 6px;
  }
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  height: 25px;
  padding-right: 8px;

  svg {
    width: 19px;
    height: 16px;
    ${({ isErrorNode, theme }) => (isErrorNode ? `fill: ${theme.colors.red300}` : '')};
  }
`;

export const BorderedWrapper = styled.div`
  background: ${p => p.theme.colors.white};
  border-radius: 5px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0 -12px 20px;
  padding: 18px 12px 4px;
`;

export const FilledWrapper = styled.div`
  background: ${p => p.theme.colors.gray200};

  border-radius: 5px;
  padding: 4px 0;

  position: relative;

  &.fade-enter {
    background: ${p => p.theme.colors.gray200};
    opacity: 0;
  }

  &.fade-enter-active {
    background: #f2f0f8;
    opacity: 1;
  }

  &.fade-enter-active {
    transition: background 500ms;
    transition: opacity 1s ease;
  }
`;

export const Content = styled.div`
  margin-bottom: 20px;
`;

export const Footer = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray400};
  padding: 20px 0 4px 0;
`;

export const InlineElementsRow = styled.div`
  display: flex;
  & > div:not(:last-child) {
    margin-right: 20px;
  }
  margin-bottom: 12px;
`;

export const SecondaryButton = styled(Button).attrs({ type: ButtonTypes.SECONDARY })`
  width: fit-content;
`;

export const ExternalLinkButton = styled.a`
  ${PrimaryButtonBase};
  text-decoration: none;
`;

export const StyledTextarea = styled(Textarea)`
  height: 68px;
  max-height: 68px;
`;

export const OptionsContainer = styled.div`
  & > div:not(:last-child) {
    margin-bottom: 12px;
  }

  display: flex;
  flex-direction: column;
`;

export const OptionsActionRow = styled(Row)`
  padding-top: 12px;
`;

export const SecondaryHeaderRow = styled(Row)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`;

export const CreateNewOptionLabel = styled.span`
  color: ${p => p.theme.colors.green400};
`;

export const InteractiveIconContainer = styled(IconContainer)`
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  ${({ disabled }) => (disabled ? `pointer-events: none` : '')}; // acts as <button> with the 'disabled' attr (un)set
  ${({ disabled, theme }) => (disabled ? `fill: ${theme.colors.gray400}` : '')}};

  &:hover {
    ${({ disabled, theme }) => (disabled ? '' : `fill: ${theme.colors.green500};`)}
`;
